import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../services/supabase';
import QRCode from '../components/QRCode';
import QRScanner from '../components/QRScanner';
import { getRandomPlaylistName } from '../utils/playlistNames';
import CookieConsent from '../components/CookieConsent';

const APP_DOMAIN = import.meta.env.VITE_APP_DOMAIN || 'tracktoss.cc';
const CREATOR_COOKIE_NAME = 'tracktoss_creator_id';

export default function Home() {
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isScannerOpen, setIsScannerOpen] = useState(false);
  const [isCookieConsentOpen, setIsCookieConsentOpen] = useState(false);
  const [playlistId, setPlaylistId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const getCreatorId = () => {
    // First try to get from cookie
    const cookies = document.cookie.split(';');
    const creatorCookie = cookies.find(cookie => cookie.trim().startsWith(`${CREATOR_COOKIE_NAME}=`));
    if (creatorCookie) {
      return creatorCookie.split('=')[1];
    }
    
    // Fallback to localStorage
    return localStorage.getItem(CREATOR_COOKIE_NAME);
  };

  const setCreatorId = (id: string) => {
    // Set cookie with 25 hour expiry
    const expiryDate = new Date();
    expiryDate.setHours(expiryDate.getHours() + 25);
    document.cookie = `${CREATOR_COOKIE_NAME}=${id}; expires=${expiryDate.toUTCString()}; path=/`;
    
    // Also store in localStorage as backup
    localStorage.setItem(CREATOR_COOKIE_NAME, id);
  };

  const createPlaylist = async () => {
    try {
      const creatorId = getCreatorId();
      if (!creatorId) {
        throw new Error('No creator ID found');
      }

      // Generate a random ID for the playlist
      const id = crypto.randomUUID();

      // Create playlist in Supabase
      const { error } = await supabase.from('playlists').insert({
        id,
        name: getRandomPlaylistName(),
        creator_id: creatorId,
        expires_at: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(),
        is_active: true,
      });

      if (error) throw error;

      const playlistUrl = `https://${APP_DOMAIN}/playlist/${id}`;
      console.log('New playlist created! URL:', playlistUrl);

      setPlaylistId(id);
      setIsModalOpen(true);
    } catch (err) {
      setError('Failed to create playlist. Please try again.');
      console.error(err);
    }
  };

  const handleCreatePlaylist = () => {
    const hasConsent = getCreatorId() !== null;
    if (hasConsent) {
      createPlaylist();
    } else {
      setIsCookieConsentOpen(true);
    }
  };

  const handleCookieAccept = () => {
    const creatorId = crypto.randomUUID();
    setCreatorId(creatorId);
    setIsCookieConsentOpen(false);
    createPlaylist();
  };

  const handleCookieDecline = () => {
    setIsCookieConsentOpen(false);
    setError('Cookies are required to create playlists. Please accept cookies to continue.');
  };

  const handleJoinPlaylist = () => {
    setIsScannerOpen(true);
  };

  const handleScan = (result: string) => {
    setIsScannerOpen(false);
    navigate(`/playlist/${result}`);
  };

  const handleShare = async () => {
    if (!playlistId) {
      console.log('Missing playlistId');
      return;
    }

    const shareUrl = `https://${APP_DOMAIN}/playlist/${playlistId}`;
    const shareData = {
      title: 'TrackToss Playlist',
      text: `🎵 Join my playlist on TrackToss! Add your favorite songs and vote on what plays next. Let's create an amazing playlist together! 🎧\n\n${shareUrl}`,
    };

    console.log('Attempting to share:', shareData);

    try {
      if (navigator.share) {
        console.log('Using Web Share API');
        await navigator.share(shareData);
      } else if (navigator.clipboard) {
        console.log('Falling back to clipboard');
        await navigator.clipboard.writeText(shareUrl);
        alert('Playlist link copied to clipboard!');
      } else {
        console.log('No sharing options available');
        alert('Your browser does not support sharing. Please copy the URL manually.');
      }
    } catch (err) {
      console.error('Error sharing:', err);
      // If sharing fails, fall back to copying to clipboard
      try {
        await navigator.clipboard.writeText(shareUrl);
        alert('Playlist link copied to clipboard!');
      } catch (copyErr) {
        console.error('Failed to copy to clipboard:', copyErr);
        alert('Failed to share playlist. Please try copying the link manually.');
      }
    }
  };

  return (
    <div className="container mx-auto max-h-[80vh] px-4 py-4 sm:py-8">
      <div className="flex min-h-screen flex-col items-center justify-center p-4">
        <div className="w-full max-w-md space-y-4">
          <button
            onClick={handleCreatePlaylist}
            className="w-full rounded-lg bg-primary px-6 py-3 text-white transition-colors hover:bg-secondary"
          >
            Create a Playlist
          </button>

          <button
            onClick={handleJoinPlaylist}
            className="w-full rounded-lg bg-primary px-6 py-3 text-white transition-colors hover:bg-secondary"
          >
            Join a Playlist
          </button>
        </div>

        {error && <div className="mt-4 rounded-lg bg-red-100 p-4 text-red-700">{error}</div>}

        {/* Cookie Consent Modal */}
        <CookieConsent
          isOpen={isCookieConsentOpen}
          onAccept={handleCookieAccept}
          onDecline={handleCookieDecline}
        />

        {/* QR Code Modal */}
        {isModalOpen && playlistId && (
          <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 p-4">
            <div className="w-full max-w-md rounded-lg bg-white p-6 dark:bg-gray-800">
              <h2 className="mb-4 text-2xl font-bold">Playlist Created!</h2>
              <div className="mb-4 flex justify-center">
                <QRCode value={`https://${APP_DOMAIN}/playlist/${playlistId}`} />
              </div>
              <div className="flex justify-center space-x-4">
                <button
                  onClick={handleShare}
                  className="rounded bg-primary px-4 py-2 text-white hover:bg-secondary"
                >
                  Share
                </button>
                <button
                  onClick={() => navigate(`/playlist/${playlistId}`)}
                  className="rounded bg-primary px-4 py-2 text-white hover:bg-secondary"
                >
                  Open
                </button>
                <button
                  onClick={() => setIsModalOpen(false)}
                  className="rounded bg-gray-200 px-4 py-2 text-gray-800 hover:bg-gray-300 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        {/* QR Scanner Modal */}
        {isScannerOpen && (
          <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 p-4">
            <div className="w-full max-w-md rounded-lg bg-white p-6 dark:bg-gray-800">
              <h2 className="mb-4 text-2xl font-bold">Scan QR Code</h2>
              <QRScanner onScan={handleScan} />
              <button
                onClick={() => setIsScannerOpen(false)}
                className="mt-4 w-full rounded bg-gray-200 px-4 py-2 text-gray-800 hover:bg-gray-300 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600"
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
